/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 UNESP Universidade Estadual Paulista "Júlio de Mesquita Filho"
 * Copyright 2024-2026 UNICAMP Universidade Estadual de Campinas
 *
 */

import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core'
import { Inscricao } from 'src/app/models/inscricao'
import { InscricaoService } from 'src/app/services/inscricao.service'
import { ConcursoService } from 'src/app/services/concurso.service'
import { MatTableDataSource } from '@angular/material/table'
import { MatSort } from '@angular/material/sort'
import { ActivatedRoute, Router } from '@angular/router'
import { UnespCoreAuthService, UnespCoreMessageService } from 'src/libs/unesp-core'
import { fromEvent, debounceTime, distinctUntilChanged, tap } from 'rxjs'
import { filter } from 'rxjs/operators'
import { DatePipe } from '@angular/common'
import { LocalStorageService } from 'src/app/services/local-storage.service'
import { TipoSituacao } from 'src/app/enums/tipo-situacao'
import { SearchInputComponent, DefaultPaginatorComponent } from 'src/app/modules/shared'
import { getStatusHeteroidentificacaoDescricao, getStatusHeteroidentificacaoDetalhado } from 'src/app/enums/status-heteroidentificacao'
import { MatDialog } from '@angular/material/dialog'
import { HeteroidentificacaoService } from 'src/app/services/heteroidentificacao.service'

@Component({
  selector: 'app-lista-inscricao',
  templateUrl: './lista-inscricao.component.html',
  styleUrls: ['./lista-inscricao.component.css'],
})
export class ListaInscricaoComponent implements AfterViewInit, OnInit {
  @ViewChild('paginator', { static: true }) paginator!: DefaultPaginatorComponent
  @ViewChild(MatSort) sort!: MatSort
  @ViewChild('searchInput') searchInput!: SearchInputComponent
  totalElements: number = 0
  sortField = 'id'
  sortDirection = 'desc'

  colunasGrid: string[] = [
    'id',
    'concurso',
    'inscricao',
    'candidato',
    'contato',
    'status',
    'pago',
    'outros',
    'action',
    'boleto',
    'pix',
  ]
  colunasGridPTPP: string[] = [
    'id',
    'concurso',
    'inscricao',
    'candidato',
    'contato',
    'status',
    'statusHeteroidentificacao',
    'pago',
    'outros',
    'action',
    'boleto',
    'pix',
  ]
  dataSource: MatTableDataSource<Inscricao> = new MatTableDataSource()

  idConcurso? = undefined
  pago: number = 0
  isDepartamento: boolean = false
  isPTPP: boolean = false
  concursoProcessoHetero?: string
  concursoEditalHetero?: string

  parametroPagina: number | undefined = 1
  parametroTamanho: number | undefined = 10

  datepipe: DatePipe = new DatePipe('pt-BR')
  filtroSituacaoSelecionada = TipoSituacao.TODOS.valueOf()
  tipoSituacao = TipoSituacao

  constructor(
    private inscricaoService: InscricaoService,
    private concursoService: ConcursoService,
    private route: ActivatedRoute,
    private unespCoreAuthService: UnespCoreAuthService,
    private unespCoreMessageService: UnespCoreMessageService,
    private localStorageService: LocalStorageService,
    private router: Router,
    private dialog: MatDialog,
    private heteroidentificacaoService: HeteroidentificacaoService
  ) {}

  ngOnInit(): void {
    this.isDepartamento = this.unespCoreAuthService.hasPermissionBasedRoles(['DEPARTAMENTO'])
    ;(this.route.parent?.params || this.route.params).subscribe(params => {
      this.idConcurso = params['id'] || params['idConcurso']
      this.pago = params['pago'] === undefined ? 0 : params['pago'] === 'pago' ? 0 : 1

      this.parametroPagina = Number(this.route.snapshot.queryParamMap.get('pagina'))
      this.parametroTamanho = Number(this.route.snapshot.queryParamMap.get('tamanho'))
      
      const filtroSalvo = this.localStorageService.get('lista-inscricao.filtro-situacao')
      // Valida se o filtro salvo existe no enum atual
      if (filtroSalvo && Object.keys(TipoSituacao).includes(filtroSalvo)) {
        this.filtroSituacaoSelecionada = filtroSalvo
      } else {
        this.filtroSituacaoSelecionada = 'TODOS'
      }

      if (this.idConcurso) {
        this.concursoService.get(this.idConcurso).subscribe((concurso: any) => {
          this.isPTPP = concurso.tipo === 'PTPP'
          this.concursoProcessoHetero = concurso.processoHeteroidentificacao
          this.concursoEditalHetero = concurso.editalHeteroidentificacao
        })
      }
    })
  }

  ngAfterViewInit() {
    // server-side search
    fromEvent<KeyboardEvent>(this.searchInput.inputRef.nativeElement, 'keyup')
      .pipe(
        debounceTime(150),
        filter((e: KeyboardEvent) => e.keyCode === 13),
        distinctUntilChanged(),
        tap(() => {
          this.paginator.pageIndex = 0
          this.getData()
        })
      )
      .subscribe()

    // on sort change, reset to first page and reload
    this.sort.sortChange.subscribe(() => {
      this.sortField = this.sort.active
      this.sortDirection = this.sort.direction || 'asc'
      this.paginator.pageIndex = 0
      this.getData()
    })

    // on paginate events, load a new page
    this.paginator?.page.pipe(tap(() => this.getData())).subscribe()

    setTimeout(() => {
      this.searchInput.inputRef.nativeElement.value = this.localStorageService.get('lista-inscricao.search') ?? ''

      if (Number.isInteger(this.parametroPagina)) {
        this.paginator.pageIndex = this.parametroPagina!
      } else {
        this.paginator.pageIndex = 0
      }
      if (Number.isInteger(this.parametroTamanho)) {
        this.paginator.pageSize = this.parametroTamanho!
      }

      this.getData()
    }, 0)
  }

  public getData() {
    this.localStorageService.set('lista-inscricao.filtro-situacao', this.filtroSituacaoSelecionada)
    this.localStorageService.set('lista-inscricao.search', this.searchInput?.value)
    this.inscricaoService
      .listar(
        this.idConcurso,
        this.filtroSituacaoSelecionada.valueOf(),
        'TODOS',
        this.pago,
        this.searchInput?.value,
        this.paginator.pageIndex,
        this.paginator.pageSize,
        this.sortField,
        this.sortDirection
      )
      .subscribe(data => {
        this.dataSource = new MatTableDataSource(data['content'])
        this.totalElements = data['totalElements']
      })
  }

  showEmitirBoleto(inscricao: Inscricao): boolean {
    if (this.unespCoreAuthService.hasPermissionBasedRoles(['RH', 'DTA', 'FINANCAS']) && inscricao.valor != 0) {
      //return !inscricao.boletoSolicitado && inscricao.pagamentoConfigurado == 'BOLETO'
      //return !inscricao.boletoSolicitado && inscricao.pagamentoConfigurado !== 'PIX'
      //return !inscricao.boletoSolicitado && inscricao.pagamentoConfigurado !== 'PIX' && !inscricao.pago
      return inscricao.pagamentoConfigurado !== 'PIX' && !inscricao.pago
    }
    return false
  }

  showEmitirPix(inscricao: Inscricao): boolean {
    if (this.unespCoreAuthService.hasPermissionBasedRoles(['RH', 'DTA', 'FINANCAS']) && inscricao.valor != 0) {
      //verificação para quando o código fosse anterior a atualização
      //return !inscricao.pixSolicitado && inscricao.pagamentoConfigurado == 'PIX'
      return inscricao.pagamentoConfigurado == 'PIX' && !inscricao.pago
    }
    return false
  }

  emitirPagamento(inscricao: Inscricao, tipo: string): void {
    if (tipo === 'PIX') {
      this.inscricaoService.emitirPagamento(inscricao.id, 'PIX').subscribe(resultado => {
        if (resultado === 'Success') {
          this.unespCoreMessageService.showMessageSuccess('PIX registrado com sucesso')
        } else {
          this.unespCoreMessageService.showMessageError('Não foi possível Registrar o pix')
        }
        this.ngOnInit()
      })
    } else {
      this.inscricaoService.emitirPagamento(inscricao.id, 'BOLETO').subscribe(resultado => {
        if (resultado === 'Success') {
          this.unespCoreMessageService.showMessageSuccess('Boleto registrado com sucesso')
        } else {
          this.unespCoreMessageService.showMessageError('Não foi possível Registrar o boleto')
        }
        this.ngOnInit()
      })
    }
  }

  abrirFicha(id: number): void {
    if (this.idConcurso) {
      this.router.navigate([`/inscricao/ficha/${id}/${this.idConcurso}`], {
        queryParams: { pagina: this.paginator.pageIndex, tamanho: this.paginator.pageSize },
      })
    } else {
      this.router.navigate([`/inscricao/ficha/${id}`], {
        queryParams: { pagina: this.paginator.pageIndex, tamanho: this.paginator.pageSize },
      })
    }
  }

  exportar() {
    if (this.totalElements !== 0) {
      this.inscricaoService.gerarArquivoCsv(this.idConcurso, this.searchInput?.value)
    } else {
      this.unespCoreMessageService.showMessageError(`Não Existem registros para exportar`)
    }
  }

  isSuperAdmin(): boolean {
    return this.unespCoreAuthService.hasPermissionBasedRoles(['SUPERADMIN'])
  }

  getCelularMask(isEstrangeiro: string, celular: string): string {
    if (!celular) return '';

    const digits = celular.replace(/\D/g, '');

    if (!celular.includes('+') || celular.startsWith('+55')) {
      if (digits.length === 11) {
        return '(' + digits.substring(0, 2) + ') ' + digits.substring(2, 7) + '-' + digits.substring(7);
      } else if (digits.length === 10) {
        return '(' + digits.substring(0, 2) + ') ' + digits.substring(2, 6) + '-' + digits.substring(6);
      }
    }
    return celular;
  }
  hasNullStatusHeteroidentificacao(): boolean {
    return this.isPTPP && this.dataSource.data.some(inscricao => 
      inscricao.statusHeteroidentificacao == null || inscricao.statusHeteroidentificacao <= 3
    )
  }

  temCandidatoNaoConfirmado(): boolean {
    if (!this.idConcurso) return false
    
    // Verifica se existe algum candidato com situação NAO_CONFIRMADA
    return this.dataSource.data.some(inscricao => 
      inscricao.status === 'NAO_CONFIRMADA'
    )
  }

  podeEnviarOuBuscar(): boolean {
    return this.temDadosHetero() && !this.temCandidatoNaoConfirmado()
  }

  buscarResultadoHeteroidentificacao(): void {
    if (!this.idConcurso) {
      this.unespCoreMessageService.showMessageError('ID do concurso não encontrado')
      return
    }

    this.inscricaoService.buscarResultadoHeteroidentificacao(this.idConcurso)
      .subscribe({
        next: (response) => {
          this.unespCoreMessageService.showMessageSuccess(response.message || 'Resultado processado com sucesso')
          this.getData()
        },
        error: (error) => {
          const errorMsg = error?.error?.message || 'Erro ao processar resultado da heteroidentificação'
          this.unespCoreMessageService.showMessageError(errorMsg)
        }
      })
  }

  getColunasGrid(): string[] {
    return this.isPTPP ? this.colunasGridPTPP : this.colunasGrid
  }

  getStatusHeteroidentificacao(valor: number | null | undefined): string {
    if (this.isStatusHeteroidentificacaoAlerta(valor) != "")
      return this.isStatusHeteroidentificacaoAlerta(valor)
    if (this.isStatusHeteroidentificacaoSemFoto(valor))
      return this.isStatusHeteroidentificacaoSemFoto(valor)
    else 
    return getStatusHeteroidentificacaoDescricao(valor)
  }

  getStatusHeteroidentificacaoTooltip(valor: number | null | undefined): string {
    return getStatusHeteroidentificacaoDetalhado(valor)
  }

  isStatusHeteroidentificacaoAlerta(valor: number | null | undefined): string {
    return (valor != null && valor == 0) ? "Inscrição não enviada" : ""
  }

  isStatusHeteroidentificacaoSemFoto(valor: number | null | undefined): string {
    return (valor != null && valor == 1) ? "Foto não enviada" : ""
  }

  temDadosHetero(): boolean {
    return !!(this.concursoProcessoHetero && this.concursoEditalHetero)
  }

  buscarIdentificador() {
    if (!this.idConcurso) return

    this.abrirModalBusca()
  }

  abrirModalBusca() {
    import('../inscricao-situacao/heteroidentificacao-modal/heteroidentificacao-modal.component').then(m => {
      const dialogRef = this.dialog.open(m.HeteroidentificacaoModalComponent, {
        width: '600px',
        data: { idConcurso: this.idConcurso }
      })

      dialogRef.afterClosed().subscribe(result => {
        if (result?.sucesso && this.idConcurso) {
          this.concursoService.get(this.idConcurso).subscribe((concurso: any) => {
            this.concursoProcessoHetero = concurso.processoHeteroidentificacao
            this.concursoEditalHetero = concurso.editalHeteroidentificacao
          })
        }
      })
    })
  }

  enviarCandidatos() {
    if (!this.temDadosHetero()) {
      this.unespCoreMessageService.showMessageError('Sincronize com o sistema de heteroidentificação antes de enviar candidatos')
      return
    }

    if (!this.idConcurso) {
      this.unespCoreMessageService.showMessageError('ID do concurso não encontrado')
      return
    }

    this.heteroidentificacaoService.enviarCandidatos(this.idConcurso).subscribe({
      next: (response) => {
        // Abre o modal com o resumo
        import('../inscricao-situacao/resumo-envio-heteroidentificacao-modal/resumo-envio-heteroidentificacao-modal.component').then(m => {
          this.dialog.open(m.ResumoEnvioHeteroidentificacaoModalComponent, {
            width: '700px',
            data: response,
            disableClose: false
          })
        })
        
        this.getData() // Atualiza a lista
      },
      error: (error) => {
        const errorMsg = error?.error?.message || 'Erro ao comunicar com sistema de heteroidentificação'
        this.unespCoreMessageService.showMessageError(errorMsg)
      }
    })
  }

  buscarResultados() {
    if (!this.temDadosHetero()) {
      this.unespCoreMessageService.showMessageError('Sincronize com o sistema de heteroidentificação antes de buscar resultados')
      return
    }

    if (!this.idConcurso) {
      this.unespCoreMessageService.showMessageError('ID do concurso não encontrado')
      return
    }

    this.heteroidentificacaoService.buscarResultados(this.idConcurso).subscribe({
      next: (response) => {
        if (response.sucesso) {
          this.unespCoreMessageService.showMessageSuccess(response.message || 'Resultados processados com sucesso')
          this.getData() // Atualiza a lista
        } else {
          this.unespCoreMessageService.showMessageError(response.message || 'Erro ao buscar resultados')
        }
      },
      error: (error) => {
        const errorMsg = error?.error?.message || 'Erro ao comunicar com sistema de heteroidentificação'
        this.unespCoreMessageService.showMessageError(errorMsg)
      }
    })
  }

  // Métodos para o stepper de progresso
  getStepClass(step: number): string {
    switch (step) {
      case 1:
        return this.temDadosHetero() ? 'completed' : 'pending'
      case 2:
        const statusCandidatos = this.getStatusCandidatosEnviados()
        return statusCandidatos === 'completo' ? 'completed' : statusCandidatos === 'parcial' ? 'partial' : 'pending'
      case 3:
        const statusFotos = this.getStatusFotosEnviadas()
        return statusFotos === 'completo' ? 'completed' : statusFotos === 'parcial' ? 'partial' : 'pending'
      case 4:
        const statusResultados = this.getStatusResultados()
        return statusResultados === 'completo' ? 'completed' : statusResultados === 'parcial' ? 'partial' : 'pending'
      default:
        return 'pending'
    }
  }

  getStatusCandidatosEnviados(): 'completo' | 'parcial' | 'pendente' {
    if (!this.temDadosHetero() || this.dataSource.data.length === 0) return 'pendente'
    
    const candidatosEnviados = this.dataSource.data.filter(i => 
      i.statusHeteroidentificacao != null && (i.statusHeteroidentificacao & 1) !== 0
    ).length
    
    const total = this.dataSource.data.length
    
    if (candidatosEnviados === 0) return 'pendente'
    if (candidatosEnviados === total) return 'completo'
    return 'parcial'
  }

  getDescricaoCandidatosEnviados(): string {
    if (!this.temDadosHetero() || this.dataSource.data.length === 0) return 'Nenhum candidato enviado'
    
    const candidatosEnviados = this.dataSource.data.filter(i => 
      i.statusHeteroidentificacao != null && (i.statusHeteroidentificacao & 1) !== 0
    ).length
    
    const total = this.dataSource.data.length
    
    if (candidatosEnviados === 0) return 'Nenhum candidato enviado'
    if (candidatosEnviados === total) return `Todos os ${total} candidatos enviados`
    return `${candidatosEnviados} de ${total} candidatos enviados`
  }

  getStatusFotosEnviadas(): 'completo' | 'parcial' | 'pendente' {
    if (!this.temDadosHetero() || this.dataSource.data.length === 0) return 'pendente'
    
    const fotosEnviadas = this.dataSource.data.filter(i => 
      i.statusHeteroidentificacao != null && (i.statusHeteroidentificacao & 2) !== 0
    ).length
    
    const total = this.dataSource.data.length
    
    if (fotosEnviadas === 0) return 'pendente'
    if (fotosEnviadas === total) return 'completo'
    return 'parcial'
  }

  getDescricaoFotosEnviadas(): string {
    if (!this.temDadosHetero() || this.dataSource.data.length === 0) return 'Nenhuma foto enviada'
    
    const fotosEnviadas = this.dataSource.data.filter(i => 
      i.statusHeteroidentificacao != null && (i.statusHeteroidentificacao & 2) !== 0
    ).length
    
    const total = this.dataSource.data.length
    
    if (fotosEnviadas === 0) return 'Nenhuma foto enviada'
    if (fotosEnviadas === total) return `Todas as ${total} fotos enviadas`
    return `${fotosEnviadas} de ${total} fotos enviadas`
  }

  getStatusResultados(): 'completo' | 'parcial' | 'pendente' {
    if (!this.temDadosHetero() || this.dataSource.data.length === 0) return 'pendente'
    
    const comResultado = this.dataSource.data.filter(i => 
      i.statusHeteroidentificacao != null && (i.statusHeteroidentificacao & 0xF0) !== 0
    ).length
    
    const total = this.dataSource.data.length
    
    if (comResultado === 0) return 'pendente'
    if (comResultado === total) return 'completo'
    return 'parcial'
  }

  getDescricaoResultados(): string {
    if (!this.temDadosHetero() || this.dataSource.data.length === 0) return 'Nenhum resultado recebido'
    
    const comResultado = this.dataSource.data.filter(i => 
      i.statusHeteroidentificacao != null && (i.statusHeteroidentificacao & 0xF0) !== 0
    ).length
    
    const total = this.dataSource.data.length
    
    if (comResultado === 0) return 'Nenhum resultado recebido'
    if (comResultado === total) return `Todos os ${total} resultados recebidos`
    return `${comResultado} de ${total} resultados recebidos`
  }

  // Métodos de validação para botões
  algumCandidatoEnviado(): boolean {
    if (!this.temDadosHetero() || this.dataSource.data.length === 0) return false
    return this.dataSource.data.some(i => 
      i.statusHeteroidentificacao != null && (i.statusHeteroidentificacao & 1) !== 0
    )
  }

  todosCandidatosEnviados(): boolean {
    if (!this.temDadosHetero() || this.dataSource.data.length === 0) return false
    return this.dataSource.data.every(i => 
      i.statusHeteroidentificacao != null && (i.statusHeteroidentificacao & 1) !== 0
    )
  }

  todasFotosEnviadas(): boolean {
    if (!this.temDadosHetero() || this.dataSource.data.length === 0) return false
    return this.dataSource.data.every(i => 
      i.statusHeteroidentificacao != null && (i.statusHeteroidentificacao & 2) !== 0
    )
  }

  podeEnviarCandidatos(): boolean {
    return this.temDadosHetero() && 
           !this.temCandidatoNaoConfirmado() && 
           !this.todosCandidatosEnviados()
  }

  podeEnviarFotos(): boolean {
    return this.temDadosHetero() && 
           !this.temCandidatoNaoConfirmado() && 
           this.algumCandidatoEnviado() && 
           !this.todasFotosEnviadas()
  }

  podeBuscarResultados(): boolean {
    return this.temDadosHetero() && this.algumCandidatoEnviado()
  }

  getTooltipSincronizar(): string {
    if (this.algumCandidatoEnviado()) {
      return 'Não é possível ressincronizar após enviar candidatos'
    }
    if (this.temDadosHetero()) {
      return 'Ressincronizar com o sistema de heteroidentificação'
    }
    return 'Sincronizar com o sistema de heteroidentificação'
  }

  getTooltipEnviarCandidatos(): string {
    if (!this.temDadosHetero()) return 'Sincronize com o sistema de heteroidentificação para habilitar'
    if (this.temCandidatoNaoConfirmado()) return 'Existem candidatos com situação Aguardando Análise. Defina a situação de todos os candidatos antes de enviar'
    if (this.todosCandidatosEnviados()) return 'Todos os candidatos já foram enviados'
    return 'Enviar candidatos para o sistema de heteroidentificação'
  }

  getTooltipEnviarFotos(): string {
    if (!this.temDadosHetero()) return 'Sincronize com o sistema de heteroidentificação para habilitar'
    if (this.temCandidatoNaoConfirmado()) return 'Existem candidatos com situação Aguardando Análise. Defina a situação de todos os candidatos antes de enviar'
    if (!this.algumCandidatoEnviado()) return 'Envie os candidatos antes de enviar as fotos'
    if (this.todasFotosEnviadas()) return 'Todas as fotos já foram enviadas'
    return 'Enviar fotos dos candidatos para o sistema de heteroidentificação'
  }

  getTooltipBuscarResultados(): string {
    if (!this.temDadosHetero()) return 'Sincronize com o sistema de heteroidentificação para habilitar'
    if (!this.algumCandidatoEnviado()) return 'Envie os candidatos antes de buscar resultados'
    return 'Buscar e processar resultados da heteroidentificação'
  }
}
